import { useState } from "react";
import type { QuadraComAnalise } from "@/types/cta-mapa-ui";
import { humanizeCode, nivelBadge } from "@/config/cta-mapa-config";

interface PilotBlocksTableProps {
  quadras: QuadraComAnalise[];
  loading: boolean;
}

const th = "px-3 py-2 text-[10px] font-semibold text-foreground-600 uppercase tracking-wider whitespace-nowrap";

function pct(v: number | null | undefined): string {
  return v == null ? "—" : `${v.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;
}

function Nivel({ code }: { code: string | null | undefined }) {
  if (!code) return <span className="text-[10px] text-foreground-400">—</span>;
  return <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${nivelBadge(code)}`}>{humanizeCode(code)}</span>;
}

/** Quadras operacionais da area piloto, com a inspecao territorial (FITA) mais recente de cada uma. */
export default function PilotBlocksTable({ quadras, loading }: PilotBlocksTableProps) {
  const [search, setSearch] = useState("");
  const [analise, setAnalise] = useState<"" | "com" | "sem">("");

  const filtered = quadras.filter(({ quadra, analise: a }) => {
    if (analise === "com" && !a) return false;
    if (analise === "sem" && a) return false;
    if (search) {
      const s = search.toLowerCase();
      if (!quadra.code.toLowerCase().includes(s) && !(quadra.name ?? "").toLowerCase().includes(s)) return false;
    }
    return true;
  });

  return (
    <div className="bg-white rounded-card border border-background-200/70 overflow-hidden flex flex-col h-full">
      <div className="px-4 py-3 border-b border-background-200/70 flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="flex items-center gap-2">
          <i className="ri-grid-line text-primary-500 text-sm"></i>
          <h3 className="text-sm font-semibold text-foreground-800 font-heading">Quadras da Área Piloto</h3>
          <span className="text-[10px] font-medium text-foreground-500 bg-background-100 px-2 py-0.5 rounded-full">
            {filtered.length} de {quadras.length}
          </span>
        </div>
        <div className="flex gap-2 sm:ml-auto">
          <input
            type="text"
            placeholder="Buscar quadra..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="text-xs border border-background-200 rounded-md px-2.5 py-1.5 bg-white text-foreground-700 focus:outline-none focus:border-accent-400 w-36"
          />
          <select
            value={analise}
            onChange={(e) => setAnalise(e.target.value as "" | "com" | "sem")}
            className="text-xs border border-background-200 rounded-md px-2 py-1.5 bg-white text-foreground-700 focus:outline-none focus:border-accent-400"
          >
            <option value="">Todas</option>
            <option value="com">Com análise FITA</option>
            <option value="sem">Sem análise FITA</option>
          </select>
        </div>
      </div>

      <div className="overflow-auto flex-1">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-background-50 border-b border-background-200/70">
              <th className={th}>Quadra</th>
              <th className={`${th} text-right`}>Área (m²)</th>
              <th className={`${th} text-right`}>Exemplares</th>
              <th className={th}>Inspeção FITA</th>
              <th className={`${th} text-right`}>Copa</th>
              <th className={`${th} text-right`}>Impermeab.</th>
              <th className={th}>Ilha de calor</th>
              <th className={th}>Déficit</th>
              <th className={`${th} text-right`}>Plantio</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-background-200/50">
            {filtered.map(({ quadra, analise: a }) => (
              <tr key={quadra.id} className="hover:bg-background-50/50 transition-colors">
                <td className="px-3 py-2">
                  <span className="text-xs font-mono font-medium text-foreground-800">{quadra.code}</span>
                </td>
                <td className="px-3 py-2 text-right text-xs text-foreground-700">
                  {quadra.areaM2 != null ? quadra.areaM2.toLocaleString("pt-BR", { maximumFractionDigits: 0 }) : "—"}
                </td>
                <td className="px-3 py-2 text-right text-xs font-medium text-foreground-700">{quadra.totalTrees.toLocaleString("pt-BR")}</td>
                <td className="px-3 py-2">
                  {a ? (
                    <span className="text-[10px] font-mono text-foreground-600 whitespace-nowrap">{a.inspectionCode}</span>
                  ) : (
                    <span className="text-[10px] text-foreground-400">Sem análise</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right text-xs text-foreground-700">{pct(a?.treeCanopyPercent)}</td>
                <td className="px-3 py-2 text-right text-xs text-foreground-700">{pct(a?.imperviousAreaPercent)}</td>
                <td className="px-3 py-2"><Nivel code={a?.heatIslandRisk} /></td>
                <td className="px-3 py-2"><Nivel code={a?.treeDeficitLevel} /></td>
                <td className="px-3 py-2 text-right text-xs text-foreground-700">
                  {a?.plantingPotentialCount != null ? a.plantingPotentialCount.toLocaleString("pt-BR") : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filtered.length === 0 && (
          <div className="p-6 text-center">
            <p className="text-xs text-foreground-500">
              {loading
                ? "Carregando quadras…"
                : quadras.length === 0
                  ? "Nenhuma quadra operacional promovida para esta área piloto."
                  : "Nenhuma quadra encontrada com os filtros atuais."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
